<script setup lang="ts">
    import { InputRow, MultiCombobox, Combobox } from '@/components/ui/form';
    import { Input } from '@/components/ui/input';
    import { useCompanyClientData } from '@/composables/useCompanyClientData';
    import { useCustomerEmployeeData } from '@/composables/useCustomerEmployeeData';
    import { useI18n } from 'vue-i18n';
    import { usePage } from '@inertiajs/vue3';
    import { computed } from 'vue';
    import type { SharedData, SelectOption } from '@/types';
    import CreditCardInput from '@/components/CreditCardInput.vue';

    const { t, tm } = useI18n();
    const page = usePage<SharedData>();
    const selectedCompanyId = computed(() => page.props.auth.user.selected_company_id);

    const { customerEmployees } = useCustomerEmployeeData(selectedCompanyId.value);
    const { clientOptions, isPendingClientOptions } = useCompanyClientData(selectedCompanyId.value);

    const typeOptions = computed<SelectOption[]>(() => {
        const map = tm('credit-cards.types') as Record<string, string>;
        return Object.entries(map).map(([value, label]) => ({ value, label }));
    });

    defineProps<{
        form: any;
    }>();
</script>

<template>
    <div class="grid gap-3 sm:grid-cols-1">
        <InputRow
            :form="form"
            name="name"
            label="credit-cards.form.name"
        >
            <Input
                id="name"
                v-model="form.name"
                :placeholder="t('credit-cards.form.name')"
            />
        </InputRow>

        <InputRow
            :form="form"
            name="number"
            label="credit-cards.form.number"
        >
            <Input
                v-if="form.id"
                v-model="form.number"
                disabled
            />
            <CreditCardInput
                v-else
                v-model="form.number"
            />
        </InputRow>

        <InputRow
            :form="form"
            name="type"
            label="credit-cards.form.type"
        >
            <Combobox
                v-model="form.type"
                :options="typeOptions"
                :placeholder="t('credit-cards.form.type')"
            />
        </InputRow>

        <InputRow
            :form="form"
            name="employee_ids"
            label="credit-cards.form.employee_ids"
        >
            <MultiCombobox
                v-model="form.employee_ids"
                :options="customerEmployees"
                :placeholder="t('credit-cards.form.employee_ids')"
                label-by="full_name"
                value-by="id"
            />
        </InputRow>

        <InputRow
            :form="form"
            name="user_ids"
            label="credit-cards.form.user_ids"
        >
            <MultiCombobox
                v-model="form.user_ids"
                :options="clientOptions"
                :placeholder="t('credit-cards.form.user_ids')"
                label-by="name"
                value-by="id"
                :loading="isPendingClientOptions"
            />
        </InputRow>
    </div>
</template>
